/**
 * Minimal entry point for connector display utilities.
 *
 * Exports only what is needed to resolve connector display names and logos
 * for UI surfaces that do not require the full form-builder UI. This imports
 * from `connector-display-data` which contains only display name messages and
 * SVG icon references, completely avoiding the heavy form-builder configs,
 * feature-gate side effects, URL constants, and connector-specific UI
 * components bundled by `/ui`.
 */
import type { MessageDescriptor } from 'react-intl';

import { connectorDisplayDataMap } from './connector-display-data';
import type { ConnectorDisplayEntry } from './connector-display-data';

export type { ThirdPartyConnectorType } from './third-party-connector-form-builder/types';

export { connectorDisplayDataMap };
export type { ConnectorDisplayEntry };

/**
 * Returns the icon (React component) for a connector type, or undefined if
 * the connector type is not recognised.
 */
export const getDataSourceLogo = (
	connectorType: string,
): React.FC<{ size?: any }> | undefined => {
	if (connectorType in connectorDisplayDataMap) {
		return connectorDisplayDataMap[connectorType as keyof typeof connectorDisplayDataMap].icon;
	}
	return undefined;
};

/**
 * Returns the display name MessageDescriptor for a connector type, or an
 * empty string if the connector type is not recognised.
 *
 * The second `datasource` parameter is accepted for API compatibility with
 * the full `getDataSourceTitle` in `displayed-third-party-products`. For the
 * `webcrawler-connector` type the full version returns
 * `datasource.connectorName`; this lightweight version always returns the
 * static display name.
 */
export const getDataSourceTitle = (
	connectorType: string,
	_datasource?: unknown,
): MessageDescriptor | string => {
	if (connectorType in connectorDisplayDataMap) {
		return connectorDisplayDataMap[connectorType as keyof typeof connectorDisplayDataMap]
			.displayName;
	}
	return '';
};
